import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, DollarSign, Sparkles, Edit, Plus, User, FileText, Calendar, ShieldCheck, Mail, AlertCircle, TrendingUp, Tag, Wrench, Clock, CheckCircle } from 'lucide-react';
import { useLandlordStore } from '@/stores/useLandlordStore';
import { LoadingScreen } from '@/components/common/LoadingSpinner';

export const UnitDetails: React.FC = () => {
    const { unitId, propertyId } = useParams();
    const navigate = useNavigate();
    const {
        currentUnitDetails,
        fetchUnitDetails,
        loading
    } = useLandlordStore();

    useEffect(() => {
        if (unitId) {
            fetchUnitDetails(unitId);
        }
    }, [unitId]);

    if (loading && !currentUnitDetails) return <LoadingScreen />;

    if (!currentUnitDetails) {
        return (
            <div className="p-8 text-center space-y-4">
                <div className="flex justify-center text-gray-400">
                    <AlertCircle size={48} />
                </div>
                <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300">Unit not found</h2>
                <button
                    onClick={() => navigate(-1)}
                    className="mt-4 px-4 py-2 bg-primary dark:bg-golden text-white rounded-lg hover:opacity-90 transition-opacity"
                >
                    Go Back
                </button>
            </div>
        );
    }

    const unit = currentUnitDetails;

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'occupied': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
            case 'available': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
            case 'maintenance': return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
            case 'pending_approval': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
            default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
        }
    };

    const getMaintenanceStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'pending': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
            case 'in_progress': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
            case 'completed': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
            default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="space-y-6 pb-12">
            {/* Header */}
            <div className="flex items-center gap-4 bg-light-card dark:bg-dark-card p-4 rounded-lg shadow-sm border border-light-border dark:border-dark-border">
                <button
                    onClick={() => navigate(-1)}
                    className="p-2 rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                >
                    <ArrowLeft size={24} className="text-primary dark:text-golden" />
                </button>
                <h1 className="text-xl font-bold text-light-text dark:text-dark-text">Unit {unit.unit_number} Details</h1>
            </div>

            {/* Overview Card */}
            <div className="bg-light-card dark:bg-dark-card p-6 rounded-2xl shadow-sm border border-light-border dark:border-dark-border space-y-4">
                <div className="flex justify-between items-center mb-2">
                    <h2 className="text-2xl font-bold text-light-text dark:text-dark-text">{unit.unit_number}</h2>
                    <span className={`px-4 py-1.5 rounded-full text-sm font-bold capitalize ${getStatusColor(unit.status)}`}>
                        {unit.status.replace('_', ' ')}
                    </span>
                </div>
                
                <div className="flex items-center gap-2 text-light-subtle dark:text-dark-subtle">
                    <Home size={18} />
                    <span>{unit.unit_type}</span>
                </div>
                
                <div className="flex items-center gap-2 text-light-subtle dark:text-dark-subtle">
                    <DollarSign size={18} />
                    <span>KES {unit.rent_amount?.toLocaleString() || 0} / month</span>
                </div>
                
                {unit.amenities && unit.amenities.length > 0 && (
                    <div className="flex items-center gap-2 text-light-subtle dark:text-dark-subtle">
                        <Sparkles size={18} />
                        <span>{unit.amenities.join(', ')}</span>
                    </div>
                )}
            </div>

            {/* Lease Amendment / Billable Utilities */}
            <div className="bg-light-card dark:bg-dark-card p-6 rounded-2xl shadow-sm border border-light-border dark:border-dark-border">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold text-light-text dark:text-dark-text flex items-center gap-2">
                        <FileText size={24} /> Lease Amendment
                    </h2>
                    <button 
                        onClick={() => navigate(`/landlord/utilities?propertyId=${unit.property_id}&unitId=${unit.id}`)}
                        className="flex items-center gap-2 bg-primary/10 text-primary dark:bg-golden/10 dark:text-golden px-4 py-2 rounded-xl text-sm font-semibold hover:bg-primary/20 dark:hover:bg-golden/20 transition-colors"
                    >
                        <Edit size={16} /> Manage Lease Changes
                    </button>
                </div>
                
                {unit.billable_utilities && unit.billable_utilities.length > 0 ? (
                    <div className="space-y-4">
                        {unit.billable_utilities.map((utility: any, index: number) => (
                            <div key={index} className="flex justify-between items-center py-3 border-b border-light-border dark:border-dark-border last:border-0 last:pb-0">
                                <div className="flex items-center gap-3 text-light-text dark:text-dark-text">
                                    {utility.type === 'fixed' ? <Tag size={18} className="text-light-subtle dark:text-dark-subtle" /> : <TrendingUp size={18} className="text-light-subtle dark:text-dark-subtle" />}
                                    <span className="font-medium">
                                        {utility.name} {utility.type === 'fixed' && utility.amount && `(KES ${utility.amount})`}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <AlertCircle size={40} className="mx-auto text-light-subtle dark:text-dark-subtle mb-3" />
                        <p className="text-light-subtle dark:text-dark-subtle">No billable utilities configured</p>
                        <button 
                            onClick={() => navigate(`/landlord/utilities?propertyId=${unit.property_id}&unitId=${unit.id}`)}
                            className="mt-4 flex items-center justify-center gap-2 mx-auto bg-primary/10 text-primary dark:bg-golden/10 dark:text-golden px-4 py-2 rounded-xl text-sm font-semibold hover:bg-primary/20 dark:hover:bg-golden/20 transition-colors"
                        >
                            <Plus size={16} /> Configure Lease Terms
                        </button>
                    </div>
                )}
                
                {unit.current_tenant && (
                    <div className="mt-6 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-900/50 rounded-lg flex gap-3 text-orange-800 dark:text-orange-400 text-sm">
                        <AlertCircle size={20} className="shrink-0" />
                        <p>⚠️ Making changes to rent or utilities will require {unit.current_tenant.full_name} to accept an updated lease agreement.</p>
                    </div>
                )}
            </div>

            {/* Pending Invitation Alert */}
            {unit.pending_invitation && (
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-900/50 p-5 rounded-2xl">
                    <h3 className="font-bold text-orange-600 dark:text-orange-400 flex items-center gap-2 mb-2">
                        <Clock size={20} /> Pending Lease Confirmation
                    </h3>
                    <p className="text-light-text dark:text-dark-text mb-1">Invitation sent to: {unit.pending_invitation.tenant_email}</p>
                    <p className="text-sm text-light-subtle dark:text-dark-subtle">Sent on {formatDate(unit.pending_invitation.created_at)}</p>
                </div>
            )}

            {/* Current Tenant */}
            <div>
                <h3 className="text-xl font-bold text-light-text dark:text-dark-text mb-4 mt-2">Current Tenant</h3>
                {unit.current_tenant ? (
                    <div 
                        onClick={() => navigate(`/landlord/tenants/${unit.current_tenant!.id}`)}
                        className="bg-light-card dark:bg-dark-card p-4 rounded-xl shadow-sm border border-light-border dark:border-dark-border flex items-center gap-4 cursor-pointer hover:border-primary transition-colors"
                    >
                        <img 
                            src={unit.current_tenant.avatar_url || "https://i.pravatar.cc/150?u=default"} 
                            alt="Tenant Avatar" 
                            className="w-14 h-14 rounded-full object-cover"
                        />
                        <div className="flex-1">
                            <h4 className="font-bold text-lg text-light-text dark:text-dark-text">{unit.current_tenant.full_name}</h4>
                            <p className="text-sm text-light-subtle dark:text-dark-subtle">
                                Since {formatDate(unit.current_tenant.lease_start_date)}
                                {unit.current_tenant.lease_end_date && ` • Ends ${formatDate(unit.current_tenant.lease_end_date)}`}
                            </p>
                        </div>
                        <ArrowLeft size={20} className="text-light-subtle transform rotate-180" />
                    </div>
                ) : (
                    <div className="bg-light-card dark:bg-dark-card p-8 rounded-xl flex flex-col items-center justify-center text-light-subtle dark:text-dark-subtle border border-light-border dark:border-dark-border border-dashed">
                        <User size={48} className="mb-2 opacity-50" />
                        <p>No active tenant</p>
                    </div>
                )}
            </div>

            {/* Lease Agreements */}
            <div>
                <h3 className="text-xl font-bold text-light-text dark:text-dark-text mb-4 mt-2 flex items-center gap-2">
                    <FileText size={24} /> Lease Agreements
                </h3>
                {unit.lease_agreements && unit.lease_agreements.length > 0 ? (
                    <div className="space-y-4">
                        {unit.lease_agreements.map((lease: any) => (
                            <div key={lease.id} className="bg-light-card dark:bg-dark-card p-5 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
                                <div className="flex justify-between items-start mb-4">
                                    <h4 className="font-bold text-light-text dark:text-dark-text">{lease.tenant_name}</h4>
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${lease.status === 'accepted' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'}`}>
                                        {lease.status === 'accepted' ? <><CheckCircle size={14} /> Signed</> : <><Clock size={14} /> Pending</>}
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-4 mb-4">
                                    <div className="flex items-center gap-2 text-sm text-light-subtle dark:text-dark-subtle">
                                        <DollarSign size={16} /> KES {lease.rent_amount?.toLocaleString()}/mo
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-light-subtle dark:text-dark-subtle">
                                        <Calendar size={16} /> {formatDate(lease.lease_start_date)} - {lease.lease_end_date ? formatDate(lease.lease_end_date) : "Ongoing"}
                                    </div>
                                    {lease.version > 1 && (
                                        <div className="flex items-center gap-2 text-sm text-light-subtle dark:text-dark-subtle">
                                            <FileText size={16} /> Version {lease.version}
                                        </div>
                                    )}
                                </div>
                                <div className="pt-4 border-t border-light-border dark:border-dark-border flex justify-between items-center text-sm">
                                    <span className="text-light-subtle dark:text-dark-subtle">
                                        Issued {formatDate(lease.created_at)}
                                    </span>
                                    {/* Placeholder for download, could wire up API in the future */}
                                    <button className="flex items-center gap-2 bg-primary dark:bg-golden text-white px-4 py-2 rounded-full font-medium hover:opacity-90">
                                        Download PDF
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="bg-light-card dark:bg-dark-card p-8 rounded-xl flex flex-col items-center justify-center text-light-subtle dark:text-dark-subtle border border-light-border dark:border-dark-border border-dashed">
                        <FileText size={48} className="mb-2 opacity-50" />
                        <p>No lease agreements found</p>
                    </div>
                )}
            </div>

            {/* Maintenance History */}
            {unit.maintenance_requests && unit.maintenance_requests.length > 0 && (
                <div>
                    <h3 className="text-xl font-bold text-light-text dark:text-dark-text mb-4 mt-2 flex items-center gap-2">
                        <Wrench size={24} /> Maintenance History
                    </h3>
                    <div className="space-y-4">
                        {unit.maintenance_requests.map((req: any) => (
                            <div key={req.id} className="bg-light-card dark:bg-dark-card p-5 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
                                <div className="flex justify-between items-start mb-2">
                                    <h4 className="font-bold text-light-text dark:text-dark-text">{req.category}</h4>
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${getMaintenanceStatusColor(req.status)}`}>
                                        {req.status.replace('_', ' ').toUpperCase()}
                                    </span>
                                </div>
                                <p className="text-sm text-light-subtle dark:text-dark-subtle mb-3">{req.description}</p>
                                <p className="text-xs text-light-subtle/70 dark:text-dark-subtle/70">Reported on {formatDate(req.created_at)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Tenant History */}
            {unit.tenant_history && unit.tenant_history.length > 0 && (
                <div>
                    <h3 className="text-xl font-bold text-light-text dark:text-dark-text mb-4 mt-2 flex items-center gap-2">
                        <Clock size={24} /> Past Tenants
                    </h3>
                    <div className="space-y-3">
                        {unit.tenant_history.map((tenant: any) => (
                            <div key={tenant.id} className="bg-light-card dark:bg-dark-card p-4 rounded-xl shadow-sm border border-light-border dark:border-dark-border flex items-center gap-4">
                                <img 
                                    src={tenant.avatar_url || "https://i.pravatar.cc/150?u=default"} 
                                    alt="Tenant Avatar" 
                                    className="w-10 h-10 rounded-full object-cover"
                                />
                                <div>
                                    <h4 className="font-semibold text-light-text dark:text-dark-text">{tenant.full_name}</h4>
                                    <p className="text-xs text-light-subtle dark:text-dark-subtle">
                                        {formatDate(tenant.lease_start_date)} - {tenant.lease_end_date ? formatDate(tenant.lease_end_date) : "N/A"}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
